import { ABSTRACT_BACKGROUND } from "../../../../libs/constants";
import React from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { Alert, ConfigProvider, Input, Segmented } from "antd";
import {
  InfoCircleOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import SweetAlert from "sweetalert2";
import {
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  NAVBAR_HEIGHT_SUM,
} from "../../../constants";

interface VideoPageLayoutProps {
  title: string;
  subtitle: string;
  container: string;
  onContainerChange: (value: string) => void;
  selectContent: React.ReactNode;
  recordContent: React.ReactNode;
}

export const VideoPageLayout = ({
  title,
  subtitle,
  container,
  onContainerChange,
  selectContent,
  recordContent,
}: VideoPageLayoutProps) => {
  const [value, setValue] = React.useState<string | number>("Seleccionar");
  const containerOk = !!container && container.length >= 3;

  const handleChangeValue = (elValue: any) => {
    SweetAlert.fire({
      title: `Si cambia de opción, se borrará el video ${
        value === "Grabar" ? "grabado" : "seleccionado"
      }`,
      icon: "warning",
      confirmButtonColor: COLOR_PRIMARY,
      confirmButtonText: `${
        value === "Seleccionar" ? "Ir a Grabar" : "Ir a Seleccionar"
      }`,
      showCancelButton: true,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) setValue(elValue);
    });
  };

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: COLOR_PRIMARY, borderRadius: 8 },
        components: {
          Segmented: {
            itemSelectedBg: COLOR_PRIMARY,
            itemSelectedColor: "#fff",
            itemColor: COLOR_PRIMARY,
            trackBg: "#e6ebf2",
          },
        },
      }}
    >
      <Grid
        container
        justifyContent="center"
        sx={{
          mt: 0,
          height: `calc(100vh - ${NAVBAR_HEIGHT_SUM})`,
          overflowY: "auto",
          overflowX: "hidden",
          ...ABSTRACT_BACKGROUND,
        }}
      >
        <Grid item xs={12} sm={9} md={6} lg={5} xl={4}>
          <Paper
            variant="outlined"
            sx={{
              m: { xs: 0.5, sm: 1 },
              borderRadius: 2,
              borderColor: "#dde3ec",
              borderTop: `4px solid ${COLOR_PRIMARY}`,
              boxShadow: "0 2px 8px rgba(16, 24, 40, 0.10)",
              overflow: "hidden",
            }}
          >
            {/* Encabezado */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                py: 2,
                borderBottom: "1px solid #e6ebf2",
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 21,
                  color: "white",
                  background: `linear-gradient(135deg, ${COLOR_PRIMARY}, ${COLOR_SECONDARY})`,
                }}
              >
                <VideoCameraOutlined />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 700, fontSize: 17, color: COLOR_PRIMARY }}
                >
                  {title}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {subtitle}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: 2.5, py: 2.5 }}>
              {/* Contenedor */}
              <Typography sx={sectionLabelSx}>Contenedor</Typography>
              <Input
                value={container || ""}
                onChange={(e) => onContainerChange(e.target.value.toUpperCase())}
                placeholder="Ej. MSKU1234567"
                size="large"
                allowClear
                status={containerOk ? "" : "error"}
                style={{ fontWeight: 600, letterSpacing: 0.5 }}
              />
              {!containerOk && (
                <Typography sx={{ fontSize: 12.5, color: "#d4380d", mt: 0.75 }}>
                  Ingrese el número de contenedor (mínimo 3 caracteres) para
                  habilitar la subida.
                </Typography>
              )}

              {/* Origen */}
              <Typography sx={{ ...sectionLabelSx, mt: 3 }}>
                Origen del video
              </Typography>
              <Segmented
                block
                size="large"
                value={value}
                onChange={handleChangeValue}
                options={[
                  {
                    value: "Seleccionar",
                    icon: <UploadOutlined />,
                    label: "Seleccionar",
                  },
                  {
                    value: "Grabar",
                    icon: <VideoCameraOutlined />,
                    label: "Grabar",
                  },
                ]}
              />

              {/* Contenido */}
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e6ebf2",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  // botones de subida/grabación sin video: estilo corporativo
                  "& .ant-btn-dashed.ant-btn-dangerous": {
                    height: 52,
                    borderRadius: 8,
                    borderWidth: 2,
                    borderColor: `${COLOR_SECONDARY}99`,
                    color: COLOR_PRIMARY,
                    background: "#fff",
                    letterSpacing: 0.4,
                  },
                  "& .ant-btn-dashed.ant-btn-dangerous:hover": {
                    borderColor: `${COLOR_PRIMARY} !important`,
                    color: `${COLOR_PRIMARY} !important`,
                    background: `${COLOR_SECONDARY}14 !important`,
                  },
                  "& .ant-btn-primary": { fontWeight: 700 },
                }}
              >
                {value === "Seleccionar" ? selectContent : recordContent}
              </Box>

              {/* Aviso */}
              <Alert
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
                closable={false}
                style={{ marginTop: 20 }}
                message={`Si sale del Módulo de Videos, o si cambia a ${
                  value === "Grabar" ? "Seleccionar" : "Grabar"
                }, se borrará ${
                  value === "Grabar" ? "lo grabado" : "lo seleccionado"
                }.`}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ConfigProvider>
  );
};

const sectionLabelSx = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: "text.secondary",
  pb: 0.75,
  mb: 1,
  borderBottom: "1px solid #e6ebf2",
} as const;

